package app.alefly.core.ads

import kotlinx.serialization.Serializable

@Serializable
enum class BannerPlacement {
    TENANT_LANDING,
    QUIZ_SCREEN,      // tela de quiz ativo (challenge — abaixo dos botões de resposta)
    RESULT_SCREEN,
    RANKING_SCREEN,   // tela de ranking/leaderboard
}

@Serializable
enum class RewardedPlacement {
    QUESTION_HINT,
}

interface AdsService {
    fun loadBanner(placement: BannerPlacement): String?

    /**
     * Pre-carrega um anúncio rewarded em background para que [showRewarded] possa
     * exibi-lo instantaneamente quando o usuário precisar. Chamadas redundantes
     * (quando já há um anúncio carregado) são ignoradas.
     */
    fun preloadRewarded(placement: RewardedPlacement) {}

    /**
     * Exibe um anúncio rewarded e notifica o chamador através de três callbacks:
     *
     * - [onRewardGranted]: o usuário completou o anúncio e ganhou a recompensa.
     *   Pode ser chamado *antes* do fechamento do anúncio (e.g. AdMob dispara quando o
     *   vídeo termina, mas a tela do anúncio ainda está visível). Use apenas para
     *   conceder a recompensa na lógica de negócio (desbloquear dica, etc.).
     *
     * - [onRewardUnavailable]: o anúncio não pôde ser carregado ou exibido.
     *   Deve conceder a recompensa como fallback, ou indicar indisponibilidade.
     *
     * - [onAdDismissed]: a UI do anúncio foi completamente fechada — o usuário
     *   voltou ao app. Dispara *após* o fechamento do anúncio, seja após
     *   [onRewardGranted] ou [onRewardUnavailable]. Use para retomar timers.
     */
    fun showRewarded(
        placement: RewardedPlacement,
        onRewardGranted: () -> Unit,
        onRewardUnavailable: (() -> Unit)? = null,
        onAdDismissed: () -> Unit = {},
    )
}

object NoopAdsService : AdsService {
    override fun loadBanner(placement: BannerPlacement): String? = null

    override fun showRewarded(
        placement: RewardedPlacement,
        onRewardGranted: () -> Unit,
        onRewardUnavailable: (() -> Unit)?,
        onAdDismissed: () -> Unit,
    ) {
        onRewardGranted()
        onAdDismissed() // sem anúncio real — dismiss imediato
    }
}
